// Mirrored in applications/platform-app/components/web-analytics — change both.
import { useEffect, useRef, useState } from 'react';
import type { WebAnalyticsApi } from './api';
import { errorMessage, errorStatus } from './api';
import type { QueryBody, QueryResponse, QueryWant, WebFilter, WebRange } from './types';

/** How often a running filtered query is polled (GET .../query/{id}). */
export const QUERY_POLL_MS = 1500;

export interface WebQueryArgs<T> {
  api: WebAnalyticsApi;
  siteId: string;
  range: WebRange;
  filters: WebFilter[];
  /** What a filtered view asks POST /query for; `null` = this data ignores filters. */
  want: QueryWant | null;
  /** The rollup endpoint used when no filter chip is active. */
  unfiltered: () => Promise<T>;
  metric?: string;
  limit?: number;
  kind?: 'store' | 'company';
  enabled?: boolean;
  /** Bump to refetch with the same arguments. */
  nonce?: number;
}

export interface WebQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** HTTP status of the last error (0 when not an HTTP error). */
  errorStatus?: number;
  /** True when this result came from POST /query (filters active) — no previous period, exits 0. */
  filtered: boolean;
}

/**
 * Rollup endpoints when no filters are active; POST /query + polling every
 * 1.5 s when there are (status running | done | failed). The last good data
 * stays on screen while a new one loads (the dashboard dims it under the gem).
 */
export function useWebQuery<T>(a: WebQueryArgs<T>): WebQueryResult<T> {
  const [state, setState] = useState<WebQueryResult<T>>({ data: null, loading: a.enabled !== false, error: null, filtered: false });
  const fnRef = useRef(a.unfiltered);
  fnRef.current = a.unfiltered;
  const filtered = a.filters.length > 0 && a.want !== null;
  const key = JSON.stringify([a.siteId, a.range, filtered ? a.filters : [], a.want, a.metric, a.limit, a.kind, a.enabled, a.nonce]);

  useEffect(() => {
    if (a.enabled === false) { setState((s) => ({ ...s, loading: false })); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setState((s) => ({ ...s, loading: true, error: null }));
    const done = (data: T) => { if (!cancelled) setState({ data, loading: false, error: null, filtered }); };
    const fail = (e: unknown) => { if (!cancelled) setState((s) => ({ ...s, loading: false, error: errorMessage(e), errorStatus: errorStatus(e) })); };

    if (!filtered) {
      fnRef.current().then(done, fail);
    } else {
      const opts = { metric: a.metric, limit: a.limit };
      const handle = (r: QueryResponse) => {
        if (cancelled) return;
        if (r.status === 'done') return done(r.result as T);
        if (r.status === 'failed') return fail(new Error(r.error || 'The filtered query failed — try again'));
        timer = setTimeout(() => { a.api.queryStatus(a.siteId, r.queryId, opts).then(handle, fail); }, QUERY_POLL_MS);
      };
      const body: QueryBody = {
        ...(a.range.range === 'custom' ? { range: 'custom', from: a.range.from, to: a.range.to } : { range: a.range.range }),
        filters: a.filters,
        want: a.want as QueryWant,
        ...(a.metric ? { metric: a.metric } : {}),
        ...(a.limit ? { limit: a.limit } : {}),
        ...(a.kind ? { kind: a.kind } : {}),
      };
      a.api.query(a.siteId, body).then(handle, fail);
    }
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, a.api]);

  return state;
}
